import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree, Router } from '@angular/router';
import { PopulationQuery } from './population.query';
import { PopulationService } from './population.service';
import { Observable, iif, of } from 'rxjs';
import { switchMap, tap, map } from 'rxjs/operators';
import { LoggingService } from '../../shared/services/core/logging.service';
import { MemberQuery } from '../../account/member/member.query';

export interface PopulationGuardOptions {
  exclude?: string[];
  include?: string[];
}

/** to use
 *  {
 *    path: 'home'
 *    component: HomePageComponent,
 *    canActivate: [ PopulationGuard ]
 *    data: {
 *            exclude: ['vip', 'season ticket'],
 *            popRedirect: ['viphome', 'seasontickethome']
 *          }
 *    OR
 *    data: {
 *            include: ['vip', 'season ticket'],
 *            popRedirect: 'some/page'
 *          }
 *  }
 */
@Injectable()
export class PopulationGuard  {
  constructor(
    private loggingService: LoggingService,
    private memberQuery: MemberQuery,
    private populationQuery: PopulationQuery,
    private populationService: PopulationService,
    private router: Router
  ) {}
  exclude: string[];
  include: string[];
  popRedirect: string | string[];

  fetchDataFromRoute(route: ActivatedRouteSnapshot) {
    this.exclude = route.data.exclude as string[];
    this.include = route.data.include as string[];
    this.popRedirect = route.data.popRedirect as string | string[];

    if (!this.popRedirect === undefined || this.popRedirect === null) {
      throw new Error('you must include a redirect');
    }
    if (this.include && this.exclude) {
      throw new Error('either include or exclude populations, not both.');
    }
    if ((!this.include || !this.include.length) && (!this.exclude || !this.exclude.length)) {
      throw new Error(`the population guard for ${route.url} has no populations specified.`);
    }
    if (typeof this.popRedirect !== 'string' && this.include) {
      throw new Error('inclusive population guard can only have one redirect');
    }
    if (typeof this.popRedirect !== 'string' && this.popRedirect.length > 1 && this.popRedirect.length !== this.exclude.length) {
      throw new Error(
        'exclusive population guard needs a redirect for each excluded population or a single redirect for all populations'
      );
    }
  }

  getRedirect() {
    if (typeof this.popRedirect === 'string') {
      return this.router.parseUrl(this.popRedirect);
    } else if (this.popRedirect.length === 1) {
      return this.router.parseUrl(this.popRedirect[0]);
    } else {
      const userPop = this.memberQuery.getPopulationName();
      if (!userPop) {
        return;
      }
      const redirect = this.popRedirect[this.exclude.indexOf(userPop)];
      return this.router.parseUrl(redirect);
    }
  }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    this.fetchDataFromRoute(route);
    return this.populationService.fetchPopulations().pipe(
      switchMap(() =>
        iif(
          () => !!this.include,
          of(this.populationQuery.userInPopulation(this.include)),
          of(this.populationQuery.userNotInPopulation(this.exclude))
        )
      ),
      tap((x) =>
        this.loggingService.debug(
          'population guard pass: ',
          x.toString(),
          'population guard type: ',
          !!this.include ? 'include' : 'exclude'
        )
      ),
      map((allowed) => {
        if (allowed) {
          return true;
        }

        let redirect = this.getRedirect();
        if (!redirect) {
          return false;
        }
        return redirect;
      })
    );
  }
}
